import HeliusGate from "../components/HeliusGate";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CrosshairIcon, SpinnerGapIcon, MagnifyingGlassIcon, DropIcon, PlusIcon, CheckIcon, RadioButtonIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchWalletScout, fetchInsiderRadar, addTrackedWallet, fetchTrackedWallets } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { shortAddr, timeAgo } from "../lib/solana";

const TrackBtn = ({ wallet, label, tracked, onTracked }) => {
  const [busy, setBusy] = useState(false);
  const isTracked = tracked.has(wallet);
  const track = async () => {
    setBusy(true);
    try {
      await addTrackedWallet(getClientId(), wallet, label);
      onTracked(wallet);
      toast.success(`Tracking ${shortAddr(wallet)} on Smart Money`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't track wallet");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid={`scout-track-${wallet}`}
      onClick={track}
      disabled={busy || isTracked}
      className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold border transition-colors shrink-0 ${isTracked ? "border-neon/30 text-neon/70" : "border-neon/50 text-neon hover:bg-neon/10"}`}
    >
      {busy ? <SpinnerGapIcon size={12} className="animate-spin" /> : isTracked ? <CheckIcon size={12} weight="bold" /> : <PlusIcon size={12} weight="bold" />}
      {isTracked ? "Tracking" : "Track"}
    </button>
  );
};

export default function WalletScout() {
  return (
    <HeliusGate feature="Wallet Scout">
      <WalletScoutInner />
    </HeliusGate>
  );
}

function WalletScoutInner() {
  useSeo({
    title: "Wallet Scout — Find Smart Money Wallets | AlphaApeing",
    description: "Pull the top holders off any CA and auto-surface wallets that repeat across bonding-curve graduates.",
  });
  const qc = useQueryClient();
  const clientId = getClientId();
  const [ca, setCa] = useState("");
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [justTracked, setJustTracked] = useState(new Set());

  const trackedQ = useQuery({ queryKey: ["trackedWallets", clientId], queryFn: () => fetchTrackedWallets(clientId) });
  const tracked = new Set([...(trackedQ.data?.wallets || []).map((w) => w.address), ...justTracked]);
  const radarQ = useQuery({ queryKey: ["insiderRadar"], queryFn: fetchInsiderRadar, refetchInterval: 120000 });

  const onTracked = (w) => {
    setJustTracked((s) => new Set([...s, w]));
    qc.invalidateQueries({ queryKey: ["trackedWallets", clientId] });
  };

  const runScan = async () => {
    const addr = ca.trim();
    if (!addr) return;
    setScanning(true);
    setScan(null);
    try {
      const d = await fetchWalletScout(addr);
      setScan(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed — try again");
    } finally {
      setScanning(false);
    }
  };

  const wallets = (scan?.holders || []).filter((h) => !h.isPool);
  const trackTopTwo = async () => {
    for (const h of wallets.slice(0, 2)) {
      try {
        await addTrackedWallet(clientId, h.wallet, `Top holder ${scan.mint.slice(0, 4)}`);
        onTracked(h.wallet);
      } catch { /* skip dupes */ }
    }
    toast.success("Top 2 holders are now tracked on Smart Money");
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-up" data-testid="wallet-scout-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><CrosshairIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">WALLET <span className="text-neon">SCOUT</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl leading-relaxed">
          Two ways to find wallets worth copying: <span className="text-white font-semibold">paste any CA</span> to rip its top holders,
          or let the <span className="text-neon">Insider Radar</span> below do it automatically — it snapshots the top holders of every
          bonding-curve graduate and surfaces wallets that keep showing up early.
        </p>
      </div>

      <div className="border border-white/10 bg-card p-5 space-y-4">
        <h2 className="font-heading font-black text-lg">Scan a CA</h2>
        <div className="flex gap-2 flex-wrap">
          <input
            data-testid="scout-ca-input"
            value={ca}
            onChange={(e) => setCa(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runScan()}
            placeholder="Paste a token contract address…"
            className="flex-1 min-w-[260px] bg-black/40 border border-white/15 px-3 py-2.5 text-sm font-mono focus:border-neon/60 outline-none"
          />
          <button
            data-testid="scout-scan-btn"
            onClick={runScan}
            disabled={scanning || !ca.trim()}
            className="flex items-center gap-1.5 px-5 py-2.5 bg-neon text-black text-sm font-bold hover:bg-neon/90 disabled:opacity-50 transition-colors"
          >
            {scanning ? <SpinnerGapIcon size={15} className="animate-spin" /> : <MagnifyingGlassIcon size={15} weight="bold" />} Scan
          </button>
        </div>

        {scan && (
          <div className="space-y-2" data-testid="scout-results">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs font-mono text-muted-foreground">{wallets.length} wallets (pools filtered out)</span>
              {wallets.length >= 2 && (
                <button data-testid="scout-track-top2" onClick={trackTopTwo} className="px-3 py-1.5 text-xs font-bold bg-neon/10 border border-neon/50 text-neon hover:bg-neon/20 transition-colors">
                  ⚡ Track top 2
                </button>
              )}
            </div>
            {scan.holders.map((h) => (
              <div key={h.wallet + String(h.amount)} className={`flex items-center gap-3 border px-3 py-2 ${h.isPool ? "border-white/5 opacity-50" : "border-white/10"}`}>
                <a href={`https://solscan.io/account/${h.wallet}`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-white/90 hover:text-neon">{shortAddr(h.wallet)}</a>
                {h.isPool && <span className="text-[9px] font-mono px-1.5 py-0.5 border border-white/20 text-muted-foreground flex items-center gap-1"><DropIcon size={10} /> POOL / CURVE</span>}
                <span className="ml-auto font-mono text-xs text-muted-foreground">{h.pct != null ? `${h.pct}% supply` : "—"}</span>
                {!h.isPool && <TrackBtn wallet={h.wallet} label={`Top holder ${scan.mint.slice(0, 4)}`} tracked={tracked} onTracked={onTracked} />}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-white/10 bg-card p-5 space-y-4" data-testid="insider-radar-board">
        <div className="flex items-center gap-2 flex-wrap">
          <RadioButtonIcon size={20} weight="fill" className="text-neon" />
          <h2 className="font-heading font-black text-lg">Insider Radar</h2>
          <span className="text-[11px] font-mono text-muted-foreground">
            auto-snapshots every graduate's top holders · {radarQ.data?.graduatesScanned ?? 0} graduates scanned
          </span>
        </div>
        {radarQ.isLoading ? (
          <div className="py-10 flex justify-center"><SpinnerGapIcon size={24} className="animate-spin text-neon" /></div>
        ) : (radarQ.data?.wallets || []).length === 0 ? (
          <p className="text-xs text-muted-foreground font-mono py-6" data-testid="insider-radar-empty">
            Building the database — the radar needs a few graduations to find repeat wallets. Wallets that appear as top-10 holders in 2+ graduates will rank here automatically.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase text-muted-foreground border-b border-white/10">
                  <th className="py-2 pr-3">Wallet</th>
                  <th className="py-2 pr-3">Graduates held early</th>
                  <th className="py-2 pr-3">Became runners</th>
                  <th className="py-2 pr-3">Last seen</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {radarQ.data.wallets.map((w) => (
                  <tr key={w.wallet} className="border-b border-white/5">
                    <td className="py-2 pr-3"><a href={`https://solscan.io/account/${w.wallet}`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs hover:text-neon">{shortAddr(w.wallet)}</a></td>
                    <td className="py-2 pr-3 font-mono text-neon font-bold">{w.hits}</td>
                    <td className="py-2 pr-3 font-mono">{w.runners > 0 ? <span className="text-neon font-bold">🚀 {w.runners}</span> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-muted-foreground">{w.lastSeen ? timeAgo(w.lastSeen) : "—"}</td>
                    <td className="py-2 text-right"><TrackBtn wallet={w.wallet} label="Insider radar" tracked={tracked} onTracked={onTracked} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
